// 1Rotary — KPI trend + AI drill-down metadata
// Keyed by KPI name (matching KPI_FRAMEWORK).

const MONTHS = ["Mar 24","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan 25","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan 26","Feb"];

function n(i){ const x = Math.sin(i*12.9898 + 78.233)*43758.5453; return (x - Math.floor(x))*0.10 - 0.05; }
function makeTrend(base, slope = 0, season = 0, vola = 0.04) {
  return MONTHS.map((m, i) => {
    const trend = base * (1 + slope * (i / 24));
    const seas = 1 + season * Math.sin(i / 2);
    const noise = 1 + n(i) * (vola / 0.05);
    return { month: m, value: +(trend * seas * noise).toFixed(4), target: +(base * (1 + slope * (i / 24))).toFixed(4) };
  });
}

/* Per-KPI: id, trend, target line, drivers, AI explanation, AI Next Best Action */
export const KPI_DETAIL = {
  "Total Daily / Monthly Revenue": {
    id: "kpi-revenue-total",
    trend: makeTrend(132_000_000, 0.18, 0.08, 0.04).map(d => ({ ...d, value: Math.round(d.value), target: Math.round(d.target * 1.02) })),
    formatPretty: "peso", spark: "rising",
    drivers: [
      { label: "Physical Retail", weight: 0.66, delta: 0.082 },
      { label: "Shopee", weight: 0.18, delta: 0.142 },
      { label: "Direct B2B", weight: 0.16, delta: 0.064 },
    ],
    aiExplanation: "Revenue is +8.4% MoM, driven by Shopee acceleration (+14% YoY) and seasonal Auto-AC demand from rising heat index. The 4-month rolling average shows sustained growth, but 8 NCR branches are now at 96% achievement — risk of plateau if pre-season stock not in place.",
    confidence: 0.91,
    nba: [
      { title: "Pre-position 1,840u inverter compressors", impact: 18_400_000, owner: "Demand Planning", urgency: "high" },
      { title: "Increase Shopee 4.4 ad budget by ₱680K", impact: 4_200_000, owner: "Marketing Ops", urgency: "medium" },
      { title: "Approve premium-tier SKU mix for top 25 branches", impact: 2_400_000, owner: "Merchandising", urgency: "low" },
    ],
    related: ["/branches", "/omnichannel", "/forecast"],
  },
  "Revenue by Channel": {
    id: "kpi-channel-mix", trend: makeTrend(0.66, -0.04, 0.02, 0.02),
    formatPretty: "share", spark: "falling-physical",
    drivers: [
      { label: "Physical share", weight: 0.66, delta: -0.018 },
      { label: "Shopee share", weight: 0.18, delta: 0.024 },
      { label: "Direct share", weight: 0.16, delta: 0.012 },
    ],
    aiExplanation: "Physical share is gradually ceding to Shopee (-2pp over 12 months) — this aligns with the strategic plan. Direct B2B is the silent winner: highest GM (38%) and growing at 12% CAGR. No intervention needed; monitor Shopee saturation.",
    confidence: 0.84,
    nba: [
      { title: "Increase B2B account-manager headcount in NCR (+2 FTE)", impact: 12_400_000, owner: "VP Ops", urgency: "medium" },
      { title: "Shopee creative refresh on top-200 listings", impact: 3_800_000, owner: "Marketing Ops", urgency: "low" },
    ],
    related: ["/omnichannel"],
  },
  "Branch Stockout Rate": {
    id: "kpi-stockout-rate", trend: makeTrend(0.072, -0.32, 0.10, 0.06),
    formatPretty: "pct", spark: "falling",
    drivers: [
      { label: "A-class SKUs", weight: 0.6, delta: -0.018 },
      { label: "B-class SKUs", weight: 0.3, delta: -0.012 },
      { label: "C-class SKUs", weight: 0.1, delta: -0.008 },
    ],
    aiExplanation: "Stockout rate down from 7.8% to 4.7% in 12 months — driven by AI replenishment (ML-001) deployment and tighter Capella supplier OTD. Currently 12 critical SKUs still flagging stockout in 4-7 days; pre-season summer surge is the biggest risk window.",
    confidence: 0.93,
    nba: [
      { title: "Execute 8 AI auto-replenishment suggestions (₱14.2M, 96% confidence)", impact: 14_200_000, owner: "Demand Planning", urgency: "high" },
      { title: "Onboard secondary supplier for Inverter Compressor 1.5HP", impact: 6_800_000, owner: "Procurement", urgency: "medium" },
    ],
    related: ["/inventory", "/operations"],
  },
  "Overstock SKU Count": {
    id: "kpi-overstock", trend: makeTrend(0.124, -0.06, 0.04, 0.04),
    formatPretty: "pct", spark: "stable",
    drivers: [
      { label: "Slow-mover Consumables", weight: 0.4, delta: 0.018 },
      { label: "Phase-out HVAC", weight: 0.3, delta: -0.024 },
      { label: "Returned Auto parts", weight: 0.3, delta: 0.008 },
    ],
    aiExplanation: "Overstock is 11.8% (target <10%) — driven by ageing C-class consumables and HVAC SKUs at end of lifecycle. AI Dead-Stock detector flagged 18 SKUs worth ₱4.8M for liquidation or transfer. Phase-out workflow lagging by 6 weeks.",
    confidence: 0.86,
    nba: [
      { title: "Liquidate top-18 dead-stock SKUs on Shopee 4.4 (avg -22% discount)", impact: 4_800_000, owner: "Merchandising", urgency: "medium" },
      { title: "Accelerate phase-out of Foam Filter Roll Eco", impact: 1_240_000, owner: "Merchandising", urgency: "low" },
    ],
    related: ["/inventory", "/merchandising"],
  },
  "Inventory Turnover": {
    id: "kpi-turnover", trend: makeTrend(7.8, 0.20, 0.08, 0.03),
    formatPretty: "x", spark: "rising",
    drivers: [
      { label: "Consumables", weight: 0.18, delta: 0.16 },
      { label: "HVAC", weight: 0.32, delta: 0.12 },
      { label: "Auto", weight: 0.42, delta: 0.06 },
      { label: "Refrig", weight: 0.08, delta: -0.04 },
    ],
    aiExplanation: "Turnover at 9.4× is comfortably in the 8-12× target band. Trend up 20% YoY thanks to tighter forecast accuracy (78%) and smaller order quantities. Refrig category is the laggard at 7.2× — overstock signal worth investigating.",
    confidence: 0.92,
    nba: [
      { title: "Tighten Refrig category reorder point — review with Honeywell", impact: 2_400_000, owner: "Demand Planning", urgency: "low" },
      { title: "Lock in current turnover as 2026 baseline target (vs old 8.5×)", impact: 0, owner: "VP Ops", urgency: "low" },
    ],
    related: ["/inventory"],
  },
  "Days Sales of Inventory (DSI)": {
    id: "kpi-dsi", trend: makeTrend(42, -0.12, 0.04, 0.02),
    formatPretty: "days", spark: "falling",
    drivers: [
      { label: "Reduced safety stock A-class", weight: 0.4, delta: -2.4 },
      { label: "Faster pick at NCR DC", weight: 0.3, delta: -1.8 },
      { label: "Phase-out C-class", weight: 0.3, delta: -1.2 },
    ],
    aiExplanation: "DSI at 38.7 days within target band (30-45). 12-month trend down 8% — capital efficiency improving. Outlier categories: Refrig (54 days, overstock signal) and Consumables (24 days, tight).",
    confidence: 0.90,
    nba: [
      { title: "Rebalance Refrig safety stock — release ₱8M working capital", impact: 8_000_000, owner: "Demand Planning", urgency: "medium" },
    ],
    related: ["/inventory", "/financial"],
  },
  "Supplier On-Time Delivery": {
    id: "kpi-otd", trend: makeTrend(0.78, 0.08, 0.03, 0.04),
    formatPretty: "pct", spark: "rising",
    drivers: [
      { label: "Tier-1 suppliers (Sanden, Daikin)", weight: 0.45, delta: 0.04 },
      { label: "Tier-2 (Capella, Local)", weight: 0.40, delta: -0.02 },
      { label: "Tier-3 (Honeywell, niche)", weight: 0.15, delta: -0.08 },
    ],
    aiExplanation: "OTD at 84.2% — below the 85% target but improving. Capella & Honeywell are the two laggards (78% and 74% respectively). AI lead-time predictor (ML-005) suggests both need 3-day buffer added to planning baseline.",
    confidence: 0.88,
    nba: [
      { title: "Trigger Capella renegotiation — extend term to Net-75 in exchange for OTD SLO", impact: 6_800_000, owner: "Procurement", urgency: "high" },
      { title: "Source secondary supplier for Honeywell-supplied Refrig parts", impact: 4_200_000, owner: "Procurement", urgency: "medium" },
    ],
    related: ["/procurement", "/financial"],
  },
  "Import Lead Time Compliance": {
    id: "kpi-leadtime-comp", trend: makeTrend(0.84, 0.08, 0.03, 0.04),
    formatPretty: "pct", spark: "rising",
    drivers: [
      { label: "Pre-cleared customs program", weight: 0.5, delta: 0.06 },
      { label: "Supplier capacity (Sanden)", weight: 0.3, delta: 0.02 },
      { label: "Port congestion (Manila S.)", weight: 0.2, delta: -0.04 },
    ],
    aiExplanation: "Lead-time compliance at 91% — well above the 85% threshold. Pre-cleared customs broker contract saving ~3 days/PO. Port-congestion risk in Q2 (Mar-May) is the main exposure; consider Subic alternative routing for 25% of volume.",
    confidence: 0.89,
    nba: [
      { title: "Route 25% of Q2 imports through Subic to mitigate Manila congestion", impact: 4_800_000, owner: "Procurement", urgency: "medium" },
    ],
    related: ["/procurement"],
  },
  "Container / PO Sell-Through Rate": {
    id: "kpi-sellthrough", trend: makeTrend(0.68, 0.12, 0.04, 0.03),
    formatPretty: "pct", spark: "rising",
    drivers: [
      { label: "A-class SKU sell-through (0-30d)", weight: 0.55, delta: 0.06 },
      { label: "B-class SKU sell-through (30-60d)", weight: 0.30, delta: 0.02 },
      { label: "C-class SKU sell-through (60-90d)", weight: 0.15, delta: -0.04 },
    ],
    aiExplanation: "Container/PO sell-through at 74% at 90 days — above the 70% healthy threshold. A-class is clearing in the first 30 days (avg 92% sell-through), which is the best indicator of forecast accuracy against the buy. C-class is the drag (58% at 90 days) — driven by 3 legacy consumables SKUs that should be phased out. Tightening the MOQ-adjusted reorder logic on those 3 SKUs will lift the network number to ~78%.",
    confidence: 0.87,
    nba: [
      { title: "Phase out 3 legacy C-class consumables (Foam Filter, Manifold Gauge Dual, Charging Hose R134a)", impact: 2_400_000, owner: "Merchandising", urgency: "medium" },
      { title: "Lock MOQ-adjusted policy in ML-001 for C-class SKUs", impact: 1_800_000, owner: "Demand Planning", urgency: "low" },
    ],
    related: ["/inventory", "/procurement", "/forecast"],
  },
  "Demand Forecast Accuracy": {
    id: "kpi-forecast-acc", trend: makeTrend(0.68, 0.16, 0.02, 0.03),
    formatPretty: "pct", spark: "rising",
    drivers: [
      { label: "A-class accuracy", weight: 0.6, delta: 0.06 },
      { label: "Heat-index ML feature", weight: 0.2, delta: 0.04 },
      { label: "B/C class accuracy", weight: 0.2, delta: 0.02 },
    ],
    aiExplanation: "Forecast accuracy at 78.2% — comfortably above the 75% A-class target. Lift since ML-001 v3.4 deployment in Jan. Auto category is best (82%), Refrig lags at 76%. Adding LTO registration data should push the network average to 80%+ by Q3.",
    confidence: 0.94,
    nba: [
      { title: "Deploy LTO-registration feature in ML-003 (Refrig model)", impact: 2_800_000, owner: "Demand Planning", urgency: "medium" },
      { title: "Retrain ML-002 with Feb extreme-heat baseline", impact: 1_200_000, owner: "Demand Planning", urgency: "low" },
    ],
    related: ["/forecast", "/admin/pipeline"],
  },
  "Shopee Conversion Rate": {
    id: "kpi-shopee-conv", trend: makeTrend(0.032, 0.28, 0.06, 0.06),
    formatPretty: "pct", spark: "rising",
    drivers: [
      { label: "Listing quality score", weight: 0.5, delta: 0.018 },
      { label: "Ad spend efficiency", weight: 0.3, delta: 0.012 },
      { label: "Anchor-event halo effect", weight: 0.2, delta: 0.008 },
    ],
    aiExplanation: "Shopee CR at 4.1% — strong vs category benchmark (3.4%). Driven by AI listing-optimizer rollout in Q4 + smarter ad-spend allocation. Bottleneck: 624 listings with quality scores <0.7 need creative refresh.",
    confidence: 0.85,
    nba: [
      { title: "AI listing optimizer pass on 624 low-quality listings", impact: 4_800_000, owner: "Marketing Ops", urgency: "medium" },
      { title: "Increase ad spend on top-50 conversion-positive SKUs by ₱1.2M", impact: 6_400_000, owner: "Marketing Ops", urgency: "high" },
    ],
    related: ["/omnichannel"],
  },
  "Online Order Fill Rate": {
    id: "kpi-fill-rate", trend: makeTrend(0.92, 0.04, 0.02, 0.02),
    formatPretty: "pct", spark: "rising",
    drivers: [
      { label: "Pasig DC pick speed", weight: 0.4, delta: 0.012 },
      { label: "Inventory accuracy", weight: 0.4, delta: 0.008 },
      { label: "Shopee API uptime", weight: 0.2, delta: 0.004 },
    ],
    aiExplanation: "Fill rate at 96.1% — above the 95% target. Improvement driven by dynamic pick-staging during Shopee anchor events. Mandaue DC running tight on capacity — consider expansion.",
    confidence: 0.91,
    nba: [
      { title: "Expand Mandaue DC pick capacity by 30% (+2 staff + 1 conveyor)", impact: 3_400_000, owner: "Operations", urgency: "medium" },
    ],
    related: ["/omnichannel", "/operations"],
  },
  "Gross Margin": {
    id: "kpi-gm", trend: makeTrend(0.296, 0.08, 0.02, 0.02),
    formatPretty: "pct", spark: "rising",
    drivers: [
      { label: "Direct channel mix", weight: 0.32, delta: 0.008 },
      { label: "FX (PHP strengthening)", weight: 0.24, delta: -0.012 },
      { label: "Promo discount drag", weight: 0.28, delta: -0.014 },
      { label: "Supplier renegotiation", weight: 0.16, delta: 0.004 },
    ],
    aiExplanation: "Gross margin at 31.8% — up 2.4pp YoY. Direct channel growth lifts margin (38% vs Shopee 22%). FX is the swing factor: a ₱2 devaluation costs ~1.2pp. Promo discount drag accelerating — tighten Shopee mega-event participation.",
    confidence: 0.90,
    nba: [
      { title: "Reduce Shopee 11.11 participation to top-100 SKUs (vs 240)", impact: 2_400_000, owner: "Merchandising", urgency: "medium" },
      { title: "Hedge ₱180M Q2 USD exposure at ₱58 strike", impact: 1_800_000, owner: "CFO", urgency: "high" },
    ],
    related: ["/financial", "/merchandising"],
  },
  "Revenue per Branch": {
    id: "kpi-rev-branch", trend: makeTrend(2_400_000, 0.14, 0.08, 0.04).map(d => ({ ...d, value: Math.round(d.value), target: Math.round(d.target) })),
    formatPretty: "peso", spark: "rising",
    drivers: [
      { label: "Top quartile (NCR)", weight: 0.4, delta: 0.18 },
      { label: "Mid quartile", weight: 0.4, delta: 0.10 },
      { label: "Bottom quartile (Cotabato, Vigan)", weight: 0.2, delta: -0.04 },
    ],
    aiExplanation: "Avg revenue per branch up 14% YoY. Concentration risk: top 5 NCR branches generate 28% of network revenue. Bottom quartile (8 branches incl. Cotabato) is dragging the average — 3-year action-or-close review pending.",
    confidence: 0.88,
    nba: [
      { title: "Trigger Cotabato 3-yr review (achievement <85%, declining)", impact: 2_800_000, owner: "VP Ops", urgency: "medium" },
      { title: "Replicate NCR best-practices playbook to bottom-quartile branches", impact: 5_400_000, owner: "Regional Manager", urgency: "low" },
    ],
    related: ["/branches"],
  },
  "Branch Inventory Accuracy": {
    id: "kpi-inv-acc", trend: makeTrend(0.948, 0.018, 0.01, 0.02),
    formatPretty: "pct", spark: "rising",
    drivers: [
      { label: "Cycle-count adherence", weight: 0.6, delta: 0.012 },
      { label: "POS-to-WMS sync errors", weight: 0.4, delta: -0.004 },
    ],
    aiExplanation: "Branch inventory accuracy at 96.2% — below 98% target. 14 branches below 95% threshold (incl. Calapan, Vigan, Cotabato). Likely root cause: incomplete cycle counts during peak weeks.",
    confidence: 0.83,
    nba: [
      { title: "Re-train branch teams in 14 below-threshold branches", impact: 1_400_000, owner: "Regional Managers", urgency: "medium" },
      { title: "Enforce mandatory weekly A-class cycle counts (currently fortnightly)", impact: 2_200_000, owner: "Operations", urgency: "medium" },
    ],
    related: ["/branches", "/inventory"],
  },
  "Revenue per Branch Headcount": {
    id: "kpi-rev-head", trend: makeTrend(280_000, 0.12, 0.06, 0.03).map(d => ({ ...d, value: Math.round(d.value), target: Math.round(d.target) })),
    formatPretty: "peso", spark: "rising",
    drivers: [
      { label: "Top quartile (NCR Pasig, Mandaue)", weight: 0.4, delta: 0.18 },
      { label: "Mid quartile", weight: 0.4, delta: 0.08 },
      { label: "Bottom quartile", weight: 0.2, delta: 0.02 },
    ],
    aiExplanation: "Labor productivity up 12% YoY. Top branches are achieving ₱420K/staff; bottom quartile under ₱180K. Calapan, Vigan flagged for staffing review (over-staffed on slow days).",
    confidence: 0.85,
    nba: [
      { title: "Reduce schedule by 1 FTE on M/Tu in 4 bottom-quartile branches", impact: 1_840_000, owner: "Regional Managers", urgency: "low" },
      { title: "Cross-train staff in 6 mid-quartile branches", impact: 1_200_000, owner: "HR", urgency: "low" },
    ],
    related: ["/branches", "/operations"],
  },
};

// Helper: get KPI detail by KPI name (with safe fallback)
export function getKpiDetail(name) {
  return KPI_DETAIL[name];
}

// Default trend if not in KPI_DETAIL (used for sparkline column)
export function safeKpiTrend(name, actual) {
  const d = KPI_DETAIL[name];
  if (d && d.trend) return d.trend;
  // synthesize a flat trend from actual
  const base = typeof actual === "number" ? actual : 1;
  return makeTrend(base, 0, 0, 0.02);
}
